// JS-driven window drag. Tauri/Wry's CSS `-webkit-app-region: drag`
// mechanism routes through `WM_NCLBUTTONDOWN` + `HTCAPTION`, which under
// DWM's move loop can intermittently activate the overlay even with
// `WS_EX_NOACTIVATE` set. Activation steals focus from the GeoGuessr
// tab and trips browser-side `blur` events — the anti-cheat signal we
// cannot afford to emit.
//
// Instead: track pointer events ourselves and reposition the window via
// Tauri's `setPosition`, which goes through `SetWindowPos(... SWP_NOACTIVATE
// | SWP_NOZORDER)` in tao and never activates the window.
//
// Lifecycle. Listeners are attached to `window` (not the handle DOM
// element) and torn down via an AbortController. That guarantees
// cleanup on component unmount mid-drag — without it, the move
// handler can keep firing setPosition after React has detached the
// element and any remaining pointer events would be unobserved
// memory references.
//
// DPI. Pointer screen coords are in CSS pixels; `setPosition` takes
// physical pixels. We multiply by `devicePixelRatio` so the cursor
// and window stay in lockstep across DPI scales.

import { useCallback, useEffect, useRef } from "react";
import { getCurrentWindow, PhysicalPosition } from "@tauri-apps/api/window";

const INTERACTIVE_SELECTOR =
  "button, input, select, textarea, a, [data-no-drag]";

export function useDragRegion() {
  // Aborts any active drag on unmount so a pending pointermove can't
  // resurrect after the component is gone.
  const unmountAbort = useRef<AbortController | null>(null);
  useEffect(() => {
    return () => {
      unmountAbort.current?.abort();
      unmountAbort.current = null;
    };
  }, []);

  return useCallback(async (e: React.PointerEvent<HTMLElement>) => {
    if (e.button !== 0) return;
    const target = e.target as HTMLElement | null;
    if (target?.closest(INTERACTIVE_SELECTOR)) return;

    const startScreenX = e.screenX;
    const startScreenY = e.screenY;

    const win = getCurrentWindow();
    let startWinX: number;
    let startWinY: number;
    try {
      const pos = await win.outerPosition();
      startWinX = pos.x;
      startWinY = pos.y;
    } catch {
      return;
    }

    // Replace any prior in-flight drag's controller with a fresh one.
    // The unmount-effect cleanup grabs the latest controller via the ref.
    unmountAbort.current?.abort();
    const controller = new AbortController();
    unmountAbort.current = controller;
    const { signal } = controller;

    const dpr = window.devicePixelRatio || 1;
    let setPositionWarned = false;
    // RAF coalescing: at high refresh rates pointermove fires up to
    // 360 Hz on some mice. Without batching that's 360 IPC round-trips
    // per second, plenty of which would be re-ordered or queued behind
    // a slow setPosition. Keep only the latest pending coords; on the
    // next animation frame (≤ 1 frame ≈ 16 ms at 60 Hz) flush exactly
    // one setPosition. Cancelled by the same AbortController so
    // unmount mid-drag doesn't strand a queued frame.
    let pendingDx = 0;
    let pendingDy = 0;
    let lastFlushedDx = 0;
    let lastFlushedDy = 0;
    let rafId: number | null = null;

    const flush = () => {
      rafId = null;
      if (signal.aborted) return;
      // Skip the IPC if the cursor hasn't actually moved this frame
      // (frequent on jittery touchpads / pen tablets that fire a
      // pointermove with the same screen coords). Saves several
      // hundred no-op IPC round-trips over a long drag.
      if (pendingDx === lastFlushedDx && pendingDy === lastFlushedDy) {
        return;
      }
      lastFlushedDx = pendingDx;
      lastFlushedDy = pendingDy;
      win
        .setPosition(
          new PhysicalPosition(startWinX + pendingDx, startWinY + pendingDy),
        )
        .catch((err: unknown) => {
          if (!setPositionWarned) {
            setPositionWarned = true;
            // eslint-disable-next-line no-console
            console.warn("useDragRegion: setPosition failed", err);
          }
        });
    };

    const onMove = (ev: PointerEvent) => {
      pendingDx = Math.round((ev.screenX - startScreenX) * dpr);
      pendingDy = Math.round((ev.screenY - startScreenY) * dpr);
      if (rafId === null) {
        rafId = requestAnimationFrame(flush);
      }
    };

    const stop = () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
      controller.abort();
    };

    window.addEventListener("pointermove", onMove, { signal });
    window.addEventListener("pointerup", stop, { signal });
    window.addEventListener("pointercancel", stop, { signal });
    window.addEventListener("blur", stop, { signal });
  }, []);
}
